// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import Scrollbars from 'react-custom-scrollbars';
import HTML from 'react-render-html';
import { toast } from 'react-toastify';

import api from '../../api';

// Components
import User from '../User';
import Navigation from '../Navigation';
import ViewControl from '../ViewControl';
import Search from '../Search';
import Stacks from './Stacks';
import Documents from './Documents';
import FileViewer from '../FileViewer';
import MostRecent from './MostRecent';
import Meta from './Meta';
import ContextMenu from '../ContextMenu';
import SearchResults from './SearchResults';
import SearchArchives from './SearchArchives';
import TrashMeta from './TrashMeta';
import MyFields from './MyFields';
import FieldsMeta from './MyFields/FieldsMeta';
import StacksOps from './StackOps';
import DocumentRedirect from './DocumentRedirect';

// Modal Components
import CreateStack from '../Modals/CreateStack';
import SendDocument from '../Modals/SendDocument';
import SharingSettings from '../Modals/SharingSettings';
import DocumentSharing from '../Modals/DocumentSharing';
import PretrainedModel from '../Modals/PretrainedModel';
import RenameStack from '../Modals/RenameStack';
import RenameDocument from '../Modals/RenameDocument';
import MoveDocument from '../Modals/MoveDocument';
import MoveStack from '../Modals/MoveStack';
import DateRange from '../Modals/DateRange';
import BatchUpload from '../Modals/BatchUpload';
import ConfigureModule from '../Modals/Automate/ConfigureModule';
import SortingRules from '../Modals/SortingRules';
import FieldForm from '../Modals/FieldForm';
import ConfirmCaution from '../Modals/ConfirmCaution';
import TrialExpired from '../Modals/TrialExpired';
import WorkflowTemplate from '../Modals/WorkflowTemplate';
import ReportDocument from '../Modals/ReportDocument';
import AttachInfo from '../Modals/AttachInfo';

// Tips Components
import StackTips from '../Tips/StackTips';
import DocumentTips from '../Tips/DocumentTips';
import UnclassifiedTips from '../Tips/UnclassifiedTips';

// Utilities
import download from '../../utilities/downloadable';
import print from '../../utilities/printable';
// import plural from '../../utilities/plural';
import rotatePage from '../../utilities/rotatePage';
import searchDocument from '../../utilities/searchDocument';
import { decodeURL } from '../../utilities/url';
import onPages from '../../utilities/onPage';

// Styles
import '../../styles/files.css';

// Accepted formats for file upload
import uploadFormats from '../../data/formats';
import rotateTable from '../../utilities/rotateTable';
import SpliceDocument from '../Modals/SpliceDocument';

const onPage = (url) => onPages([url])

// To force route re-render
class FieldName extends React.Component {
    render = () => {
        let { field, value } = this.props.match.params;

        field = decodeURL(field);
        value = decodeURL(value);

        let title;
        if (value === "___unset___")
            title = <>Documents with <strong>{field.toUpperCase()}</strong> unset</>;
        else title = field.toUpperCase() + ": " + value;

        return (<>
            <div
                className="title-back"
                onClick={() => this.props.history.push('/fields')}
            >Back</div>
            <div title={title} className="title-text"><strong>Field:</strong> {title}</div>
            <ViewControl view={this.props.view} setView={this.props.setView} />
        </>);
    }
};

class StackName extends React.Component {
    render = () => {
        
        const path = this.props.stack.path;
        const self = path.split("/").pop();
        const parent = path.substring(0, path.length - self.length - 1);
    
        let tmp = "";
        const title = this.props.stack.path.split("/").pop();

        return (<>
            {parent.length > 0 ? <div
                className="title-nav"
            >
                <div
                    className="title-nav-component title-nav-component-link"
                    onClick={() => this.props.history.push('/')}
                >My Stacks</div>
                {parent.split("/").map((p, idx) => {
                    tmp += (tmp.length > 0 ? "/" : "") + p;
                    const tmpStack = this.props.stacks.find(s => s.path === tmp);
                    return (<React.Fragment key={idx}>&nbsp;&nbsp;•&nbsp;&nbsp;<div
                        key={idx}
                        className={"title-nav-component" + (tmpStack ? " title-nav-component-link" : "")}
                        onClick={() => {
                            if (tmpStack){
                                this.props.history.push('/stack/' + tmpStack._id)
                            }
                        }}
                    >{p}</div></React.Fragment>);
                })}
            </div> : <div
                className="title-back"
                onClick={() => {
                        this.props.history.push('/')
                        this.props.setStack({_id: null, path: ''})
                    }
                }
            >Back</div>}
            <div title={title} className="title-text">{title}</div>
            <div
                className="title-icon"
                style={{ backgroundImage: "url('https://static.stax.ai/images/icons/" + (this.props.user && this.props.user.enableDarkMode ? "dark/" : "") + "upload-title.png')" }}
                onClick={this.props.uploadFiles}
            >
                Upload file(s) to stack
            </div>
            <img
                className="hover-grow"
                src="https://static.stax.ai/images/icons/options.png"
                alt="Options"
                title="View Options"
                onClick={this.props.openMenu}
            />
            <ViewControl view={this.props.view} setView={this.props.setView} />
            <img
                className="stack-help"
                src="https://static.stax.ai/images/icons/info.png"
                alt="(i)"
                title="Show help screen"
                onClick={() => this.props.updateUser({
                    _id: this.props.user._id,
                    tips: {
                        ...this.props.user.tips,
                        stack: false
                    }
                }, false)}
            />
        </>);
    }
};

export default class MyStacks extends React.Component {

    scrollRef = React.createRef();

    state = {
        view: 'grid',
        meta: 'hide', // Hide or show

        // Root navigation
        root: {
            quick: [],
            stacks: []
        },

        // Stack
        stack: {
            _id: null,
            path: ""
        },

        // Modules (admins only)
        modules: [],
        templates: [],

        // Forms
        forms: [],

        // Stack documents
        docs: [],

        // Document
        document: {
            _id: null,
            pages: [],
            newTag: {}, 
        },

        // Statement if exists
        statement: null,

        // Fields
        fields: [],
        field: { key: "Loading..." },
        value: "",

        // Selected document(s)
        selection: [],

        // Hovered element
        hovered: null,

        // Search query
        search: '',
        searchArchive: null, // Archive query components
        matches: {
            count: 0,
            index: 0,
            words: []
        },
        searching: false,

        // Modal renderer
        modal: "",
        modalData: {},

        // Document functions
        function: "",
        functionData: {},

        editTableIdx: -1, 

        // Filter documents by fields
        filter: [],

        // Scroll position
        scrollPosition: 'not bottom',
    }

    handleScroll = () => {
        const values = this.scrollRef.current.getValues();
        this.setState({ scrollPosition: values.top > 0.98 ? 'bottom' : 'not bottom' });
    }

    loadStacks = async (modules = false) => {
        const res = await api.get('/stack/all?minimal=true' + (modules ? '&modules=true' : ''));
        if (res && res.success && res.stacks)
            this.setState({ stacks: res.stacks });
    }

    componentDidMount = async () => {

        // File upload dialog
        this.fileUploadDialog = document.createElement('input');
        this.fileUploadDialog.setAttribute('type', 'file');
        this.fileUploadDialog.setAttribute('multiple', 'multiple');

        await this.props.loading(9999, '', false, 'MyStacksIndexMount');
        await this.loadRootData();
        await new Promise((resolve, reject) => {
            const waitUserLoad = setInterval(() => {
                if(this.props.user?._id){
                    this.loadModules()
                    clearInterval(waitUserLoad);
                    resolve()
                }
            }, 100)
        })

        // this.loadModules(); // Load synchronously       
        this.loadForms(); // Load synchronously

        const path = this.props.location.pathname;
        
        // Check if the path has a valid stack._id 
        if(path.startsWith("/stack/")) {
            const stackId = path.split("/")[2];
            if(stackId && stackId.length === 24) {
                const stack = this.state.root.stacks.find(s => s._id === stackId);
                if(stack) {
                    this.setState({ stack: stack });
                }
            }
        }         
        
        // Setup keyboard shortcuts
        window.addEventListener("keydown", (event) => {
            // Escape key - clear search, close modal, deselect
            if (event.key === "Escape") {
                if (this.state.search !== "")
                    this.search("");
                else if (this.state.modal !== "") {
                    this.modal();
                } else if (!onPage("/document/"))
                    this.setState({ selection: [] });
                else if (this.state.function !== "")
                    this.setFunction(); 
            }

            // Control + F should default to search
            else if (event.key === "f" && event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
                document.getElementById("search").focus();
            }

            // else if (this.state.modal === "" && !onPage("/document/") && event.key && event.key.toLowerCase() === "a" && event.ctrlKey) {
            //     if (event.shiftKey)
            //         this.setState({ selection: [] });
            //     else this.setState({ selection: this.state.docs ? this.state.docs.map(d => d._id) : [] });
            // }
        });

        // Ensure user has billing info
        this.checkActivationStatus();
        this.props.loaded('MyStacksIndexMount');
    }

    componentWillUnmount = () => {
        if (this.statusInterval)
            clearInterval(this.statusInterval);
    }

    startStatusUpdateRoutine = async (stackId) => {
        // Clear previous interval
        if (this.statusInterval) {
            if (this.state.stack?._id !== stackId) {
                clearInterval(this.statusInterval);
                this.statusInterval = null;
            }
            else return;
        }

        if (!stackId || stackId.length !== 24)
            return;

        let firstRun = true;

        // Status update interval
        this.statusInterval = setInterval(async () => {
            // Stack changed?
            if (!firstRun && this.state.stack?._id !== stackId) {
                clearInterval(this.statusInterval);
                this.statusInterval = null;
                return;
            }
            firstRun = false;

            const res = await api.get('/stack/live?stackId=' + stackId, {}, true);
            if (!res || !res.training || !res.processing || !res.errored)
                return;

            // If no documents are processing or training, stop this routine.
            if (!res.training.length && !res.processing.length) {
                clearInterval(this.statusInterval);
                this.statusInterval = null;
                // return;
            }

            // Update stack training statuses
            const stacksInTraining = res.training.filter(s => s.trainer.isTraining).map(s => s._id);
            const stacksInQueue = res.training.filter(s => s.trainer.manualTrigger).map(s => s._id);

            // Notification if retraining of this stack finished
            if (this.state.stack && this.state.stack.trainer && (this.state.stack.trainer.isTraining || this.state.stack.trainer.manualTrigger) && !stacksInTraining.includes(this.state.stack._id) && !stacksInQueue.includes(this.state.stack._id))
                toast.info("Finished retraining stack classifier.");

            let stacks = JSON.parse(JSON.stringify(this.state.root.stacks));
            for (const stack of stacks) {
                stack.trainer.isTraining = stacksInTraining.includes(stack._id);
                stack.trainer.manualTrigger = stacksInQueue.includes(stack._id);
            }

            // Update document processing statuses
            const docsInError = res.errored.map(d => d._id);
            const docsInProcess = res.processing.filter(d => d.job && d.job.working === true).map(d => d._id);
            const docs = this.state.docs.filter(d => docsInError.includes(d._id) || docsInProcess.includes(d._id) || d.job.working === true);
            for (const doc of docs){
                
                const docUpdate = res.processing.find(d => d._id === doc._id); 
                if (docUpdate){
                    doc.job = docUpdate.job; 
                    continue  
                }

                doc.job.success = !docsInError.includes(doc._id);
                doc.job.working = docsInProcess.includes(doc._id);

                if (!doc.job){
                    doc.job = {}
                    continue  
                }
                
                // If job was in process and is now successful, refetch document meta
                if(doc.job.working || doc.pages.length === 0){
                    const docRes = await api.get('/document/get?docId=' + doc._id + '&disableReview=true&minimal=true', {}, false, {}, true); 

                    if(!docRes || !docRes.doc)
                        continue 

                    for (let field of ["name", "pages", "stack", "lastModified", "words", "urgent", "thumbnail", "reviewed"])
                        doc[field] = docRes.doc[field];
                        
                }
            }

            const activeDocUpdate = res.processing.find(d => d._id === this.state.document?._id);

            // State update
            this.setState(prev => ({
                root: {
                    ...prev.root,
                    stacks
                },
                docs: prev.docs.map(d => {
                    // Was this a document that we just checked for and updated? set it to that, otherwise the previous state
                    const updated = docs.filter(u => u._id === d._id); 
                    return updated.length > 0? updated[0]: d 
                }),
                stack: {
                    ...prev.stack,
                    trainer: {
                        ...prev.stack.trainer,
                        isTraining: stacksInTraining.includes(prev.stack._id),
                        manualTrigger: stacksInQueue.includes(prev.stack._id)
                    }
                },
                document: {
                    ...prev.document,
                    job: activeDocUpdate ? activeDocUpdate : {
                        ...prev.document.job,
                        success: !docsInError.includes(prev.document._id),
                        working: docsInProcess.includes(prev.document._id)
                    }
                }
            }));

        }, 3000);
    }

    loadRootData = async () => {
        const res = await api.get('/stack/all');
        
        // requiered for sync toast
        const moduleId = "62c4dab5bc3f9e1372bfaa9f"
        let userSyncState = false

        if (!res || !res.quick || !res.stacks)
            return;

        // sync local app toast
        if (localStorage.getItem("userLoggedIn"))
		{	
            // checking if current user is assigned to sync module under any stack    
            for (let stack of res.stacks){
                const pipeline = stack.pipeline?.find(p => p.module === moduleId)
                if (pipeline && pipeline.config[1].value === this.props.user._id) {
                    userSyncState = true
                    break
                }
            }

			// current UTC time at login
			const currentTime = new Date()

			// if last access is found and if one day has passed, show the sync toast
			if (this.props.user.lastLocalSync ?? false){
				const lastLocalSync = new Date(this.props.user.lastLocalSync)
				if (currentTime - lastLocalSync > 3600000 && userSyncState){
					toast.info("Your STAX·AI Sync app hasn't checked in for a while. Please make sure that you are still logged in to it so documents get saved.")
			    }
            }
			localStorage.removeItem("userLoggedIn")
		}


        await this.setState({
            root: {
                quick: res.quick,
                stacks: res.stacks
            }
        });
    }

    loadModules = async () => {
        const res = await api.get('/module/all');
        if (!res || !res.modules)
            return;
        await this.setState({ modules: res.modules });
    }

    loadForms = async () => {
        const res = await api.get('/form/all');
        if (!res || !res.forms)
            return;
        await this.setState({ forms: res.forms });
    }

    modal = async (modal = "", modalData = {}) => await this.setState({ modal, modalData });

    setFunction = async (f = "", d = {}) => await this.setState({
        function: f,
        functionData: d
    })

    searchDocument = (search, doc) => {
        // Ensure document meta has loaded
        if (!doc.words || doc.words.length === 0)
            return setTimeout(() => this.searchDocument(search, this.state.document), 500); // Try again in half a second

        this.setState(searchDocument(search, doc));
    }

    search = (search) => {

        const doc = this.state.document;

        if (onPage("document") && doc && search.length > 3) {

            if (this.searchTimeout)
                clearTimeout(this.searchTimeout);

            // Searching icon
            this.setState({ search, searching: !doc.format || doc.format === "doc" });

            // Immediate search if there aren't too many words
            if (doc.words && doc.words.length < 5000)
                return this.searchDocument(search, doc);

            // Else search after a timeout
            this.searchTimeout = setTimeout(() => this.searchDocument(search, doc), 200);
            return;

        }

        // Search across stacks
        this.setState({
            search,
            matches: {
                count: 0,
                index: 0,
                words: []
            }
        });
    };

    getDocument = (docId) => {
        for (const doc of this.state.docs)
            if (doc._id === docId)
                return doc;
        return {
            _id: null,
            name: ""
        };
    }

    setDocument = async (doc) => {
        await this.setState({ document: doc, selection: [doc._id] });
        this.setState({editTableIdx: -1});
        const chatWidget = document.querySelector("chat-widget");
        if (!chatWidget) return;

        const metadata = [{
            name: 'Team Name',
            value: this.props.team.name,
            render: true
        }];

        if (this.state.stack?.path)
            metadata.push({
                name: 'Stack Path',
                value: this.state.stack.path,
                render: true
            });

        if (this.state.stack?._id)
            metadata.push({
                name: 'Stack ID',
                value: this.state.stack._id,
                render: true
            });

        if (doc?.name)
            metadata.push({
                name: 'Document Name',
                value: doc.name,
                render: true
            });

        if (doc?._id) {
            metadata.push({
                name: 'Document ID',
                value: doc._id,
                render: true
            }, {
                name: 'Document Link',
                value: `https://my.stax.ai/stack/${this.state.stack._id ?? "unread"}/document/${doc._id}`,
                render: true
            });
        }
        if (typeof chatWidget?.setAttribute !== "function") return;
        chatWidget.setAttribute('metadata', JSON.stringify(metadata));
    }

    setStatement = (statement) => this.setState({ statement });

    setWords = async (docId, words) => docId === this.state.document._id ? await this.setState({
        document: {
            ...this.state.document,
            words
        }
    }) : {}

    // Rotate page words
    rotateDocumentPage = async (pageIdx, rotation, cloud = false, ignoreCurrent = false) => {
        const document = rotatePage(this.state.document, pageIdx, rotation, ignoreCurrent);

        if (!document)
            return;

        // Update state
        await this.setState({ document });

        // Cloud update
        if (!cloud)
            return;

        await this.updateDocument(this.state.document._id, { rotations: document.rotations }, true);
    }

    // Create stack
    createStack = (within) => this.modal('create-stack', within); // Open modal
    createStackExec = async (stackPath, rootStack, baseStack = null) => {

        await this.props.loading();

        const res = await api.post('/stack/create', {
            stackPath: rootStack && rootStack.length > 0 ? rootStack + "/" + stackPath : stackPath,
            baseStack,
            inheritSharingFrom: rootStack
        });

        if (res && res.stack) {
            // Add created stack to root list
            let stacks = JSON.parse(JSON.stringify(this.state.root.stacks));
            stacks.push(res.stack);
            stacks.sort((a, b) => a.path > b.path ? 1 : a.path < b.path ? -1 : 0);

            // Update local state
            await this.setState(prevState => ({
                root: {
                    ...prevState.root,
                    stacks
                }
            }));

            if (!baseStack)
                window.location.href = '/stack/' + res.stack._id;
        }

        this.props.loaded();

    }

    // Delete stack
    deleteStack = async (stackId, recursive = false) => {

        // Local delete
        let stacks = JSON.parse(JSON.stringify(this.state.root.stacks));
        const stackIdx = stacks.findIndex(s => s._id === stackId);

        // Stacks inside...
        if (recursive) {
            const root = stacks[stackIdx].path;
            let substacks = stacks.filter(s => s.path.substring(0, root.length + 1) === root + "/");
            while (substacks.length > 0) {
                const tmpStack = substacks.pop();
                const tmpIdx = stacks.findIndex(s => s._id === tmpStack._id);
                stacks.splice(tmpIdx, 1);
            }
        }

        // The stack to delete
        stacks.splice(stackIdx, 1);

        await this.setState(prevState => ({
            root: {
                ...prevState.root,
                stacks
            }
        }));

        // API update
        await api.post('/stack/remove', { stackId, recursive });

    }

    // Manage advanced sharing settings - functions inside modal
    manageSharing = () => this.modal('sharing-settings');
    manageDocumentSharing = (docId) => this.modal('document-sharing', docId);

    // Manage advanced sorting settings - functions inside modal
    manageSorting = () => this.modal('sorting-rules');

    // Rename stack
    renameStack = (stack) => this.modal('rename-stack', stack);
    renameStackExec = async (stack, path) => {

        await this.props.loading();
        // We have to rename this stack and all other stacks that are nested within this stack
        const conflict = this.state.root.stacks.find(s => s.path === path);
        if (conflict) {
            toast.error("A stack with this name already exists!");
            await this.props.loaded();
            return;
        }

        // All changes necessary for updating stack path migrated to stax-api
        // for (const s of this.state.root.stacks) {
        //     if (s._id === stack._id) continue;
        //     if (s.path.substring(0, stack.path.length) !== stack.path) continue;

        //     // Keep parent and children, but rename self
        //     const children = s.path.substring(stack.path.length);
        //     if (children.length === 0) continue;

        //     await this.updateStack(s._id, {
        //         path: path + children
        //     });
        // }  

        // Now, rename this stack
        await this.moveStackUpdated(stack._id, {
            path: path
        });

        await this.props.loaded();

        window.location.reload(false);
    }

    // Rename document
    renameDocument = (doc) => this.modal('rename-document', doc);

    // Download document
    downloadDocument = async (doc) => {
        await this.props.loading(1005, "Preparing...");

        // Download via presigned URL if no changes have been made...
        let res;
        if (doc.download && (!doc.canvas || !Object.keys(doc.canvas).length))
            res = await api.download(doc.download, {}, {}, true);
        else
            res = await api.download('/document/download?docId=' + doc._id);

        // File name cleanup
        let fileName;
        let fileNameCmps = doc.name.split(".");
        if (!doc.format || doc.format === "doc") {
            // Document/PDF file. Replace extension (if exists) with ".pdf"
            if (fileNameCmps.length === 1)
                fileName = fileNameCmps.join(".") + ".pdf";
            else
                fileName = fileNameCmps.join(".");
        }

        else // Format provided
            fileName = doc.name + "." + doc.format;

        download(res, fileName);
        this.props.loaded();
    }

    // Print document
    printDocument = async (doc) => {
        await this.props.loading(1005, "Preparing...");

        // Download via presigned URL if no changes have been made...
        let res;
        if (doc.download && (!doc.canvas || !Object.keys(doc.canvas).length))
            res = await api.download(doc.download, {}, {}, true);
        else
            res = await api.download('/document/download?docId=' + doc._id);

        print(res, "application/pdf");
        this.props.loaded();
    }

    // Send via email
    sendDocument = async (doc) => this.modal('send-document', doc);

    // Upload file dialog
    uploadFiles = async (target = null, metadata = []) => {
        this.fileUploadDialog.click();

        this.fileUploadDialog.onchange = (e) => {
            let path;
            if (e.path)
                path = e.path;
            else if (e.composedPath && e.composedPath())
                path = e.composedPath()
            else
                return; // raise toast error here?

            this.uploadFilesExec(path[0].files, target, metadata);

        }

    }

    // Upload file exec
    uploadFilesExec = async (files, target = null, metadata = []) => {

        let unsupported = [];
        let uploadFiles = [];

        let formData = new FormData();

        for (let file of files) {
            const extn = file.name.split(".").slice(-1).pop().toLowerCase();
            if (uploadFormats.includes(extn))
                uploadFiles.push(file);
            else if (!unsupported.includes(extn))
                unsupported.push(extn);
        }

        if (unsupported.length > 0)
            toast.error("Unsupported file formats: " + unsupported.map(f => f.toUpperCase()).join(", "));

        if (uploadFiles.length < 1)
            return;

        if (typeof target !== "undefined" && target !== null) {
            formData.set('stackId', target);

            // Local root list update
            let stacks = JSON.parse(JSON.stringify(this.state.root.stacks));
            const stackIdx = stacks.findIndex(s => s._id === target);
            stacks[stackIdx].numDocuments += files.length;

            // Track local change until api updates
            await this.setState(prevState => ({
                root: {
                    ...prevState.root,
                    stacks
                }
            }))
        }

        if (typeof metadata !== "undefined" && metadata && metadata.length > 0)
            formData.set('metadata', JSON.stringify(metadata));

        // if (uploadFiles.length > 5)
        return this.modal('batch-upload', {
            target,
            files: uploadFiles
        });

        // for (let file of uploadFiles)
        //     formData.append('files', file);

        // await this.props.loading(999, "Uploading...");
        // const res = await api.post('/document/upload', formData);

        // await this.loadRootData(); // Reload

        // if (res && res.uploads) {
        //     // Add temporary documents to keep track of progress
        //     if (target === this.state.stack._id || (metadata && metadata.length > 0 && onPage("/field/")))
        //         await this.addUploadsLocally(res.uploads);

        //     toast.success('Uploaded ' + uploadFiles.length + plural(' document', uploadFiles.length) + '! Stax bots will process them soon...');

        // }

        // else toast.error('Oops, something went wrong! Try again!');

        // this.props.loaded();

    }

    // Add files locally
    addUploadsLocally = async (uploads) => {
        await this.setState(prevState => ({
            docs: [
                ...uploads.map(u => ({
                    ...u,
                    job: {
                        ...u.job,
                        working: true
                    }
                })), // Add to front
                ...prevState.docs
            ]
        }));
        this.startStatusUpdateRoutine(this.state.stack._id);
    }

    // Update counts of documents in Quick Access
    updateQuickCounts = async (_id, numNewDocs, numNewUnread) => {

        // Local root list update
        let quick = JSON.parse(JSON.stringify(this.state.root.quick));
        let quickIdx = null;

        for (let i = 0; i < quick.length; i++) {
            if (quick[i]._id === _id) {
                quickIdx = i;
                break;
            }
        }

        if (quickIdx === null) {
            return;
        }

        let reviewedStack = quick[quickIdx];
        reviewedStack["numDocuments"] += numNewDocs;
        reviewedStack["numUnread"] += numNewUnread;

        quick[quickIdx] = {
            ...reviewedStack
        };

        await this.setState(prevState => ({
            root: {
                ...prevState.root,
                quick
            }
        }));
    }

    // Stack settings update
    updateStack = async (stackId, stack, cloud = true) => {

        // Local root list update
        let stacks = JSON.parse(JSON.stringify(this.state.root.stacks));
        const stackIdx = stacks.findIndex(s => s._id === stackId);

        stacks[stackIdx] = {
            ...stacks[stackIdx],
            ...stack
        };

        // Updating live stack
        if (stackId === this.state.stack._id)
            await this.setState(prevState => ({
                stack: {
                    ...prevState.stack,
                    ...stack
                }
            }));

        await this.setState(prevState => ({
            root: {
                ...prevState.root,
                stacks
            }
        }));

        if (!cloud)
            return;

        // API update
        await api.post('/stack/update', {
            stackId,
            stackDiff: {
                ...stack,
                _id: undefined
            }
        });

    }

    // Move or rename stacks
    moveStackUpdated = async (stackId, stack, cloud = true) => {

        // // Local root list update
        // let stacks = JSON.parse(JSON.stringify(this.state.root.stacks));
        // const stackIdx = stacks.findIndex(s => s._id === stackId);

        // stacks[stackIdx] = {
        //     ...stacks[stackIdx],
        //     ...stack
        // };

        // // Updating live stack
        // if (stackId === this.state.stack._id)
        //     await this.setState(prevState => ({
        //         stack: {
        //             ...prevState.stack,
        //             ...stack
        //         }
        //     }));

        // await this.setState(prevState => ({
        //     root: {
        //         ...prevState.root,
        //         stacks
        //     }
        // }));

        // if (!cloud)
        //     return;

        //API update
        await api.post('/stack/move', {
            stackId,
            stackDiff: {
                ...stack,
                _id: undefined
            }
        })
    }

    viewDoc = async (doc) => {
        let quick = JSON.parse(JSON.stringify(this.state.root.quick));
        let quickIdx = null;
        for (let i = 0; i < quick.length; i++) {
            if (quick[i]._id === "recent") {
                quickIdx = i;
                break;
            }
        }
        if (quickIdx === null) {
            return;
        }
        let reviewedStack = quick[quickIdx];
        if (reviewedStack && reviewedStack["numDocuments"] < 50) {
            await this.updateQuickCounts("recent", 1, 0);
        }
    }

    // Document settings update
    updateDocument = async (docId, doc, cloud = true, waitCloud = true) => {

        // Local root list update
        let docs = JSON.parse(JSON.stringify(this.state.docs));
        const docIdx = docs.findIndex(d => d._id === docId);
        let docDelta = 0;
        let readDelta = 0;

        if (docs[docIdx]) {

            // Check for urgency changes
            if (docs[docIdx].urgent !== doc.urgent && (typeof doc.urgent !== "undefined")) {
                docDelta = doc.urgent - docs[docIdx].urgent;
                await this.updateQuickCounts("urgent", docDelta, docDelta * !doc.reviewed);
            }

            // Check for flagged changes
            if (docs[docIdx].flagged !== doc.flagged && (typeof doc.flagged !== "undefined")) {
                docDelta = doc.flagged - docs[docIdx].flagged;
                await this.updateQuickCounts("flagged", docDelta, docDelta * !doc.reviewed);
            }

            // Check for read changes
            else if (docs[docIdx].reviewed !== doc.reviewed && (typeof doc.reviewed !== "undefined")) {
                docDelta = docs[docIdx].reviewed - doc.reviewed;
                readDelta = docDelta;
                await this.updateQuickCounts("unread", docDelta, readDelta);

                // update read counts in other quick stacks if this doc is in them
                if (docs[docIdx].urgent) {
                    await this.updateQuickCounts("urgent", 0, readDelta);
                }
                if (docs[docIdx].stack === null) {
                    await this.updateQuickCounts("unclassified", 0, readDelta);
                }
                if (docs[docIdx].assignee === this.props.user._id && typeof docs[docIdx].assignee !== "undefined") {
                    await this.updateQuickCounts("mine", 0, readDelta);
                }

                // update stack if it exists
                if (docs[docIdx].stack && readDelta !== 0) {
                    const src = this.findStack(docs[docIdx].stack);
                    await this.updateStack(docs[docIdx].stack, {
                        numUnread: src.numUnread + readDelta,
                    }, false); // Local only
                }
            }

            // Check for assignee changes
            else if (docs[docIdx].assignee !== doc.assignee && (typeof doc.assignee !== "undefined")) {

                // Check if we assigned to this user and reassigned to someone else
                if (doc.assignee === this.props.user._id) {
                    docDelta = 1;
                } else {
                    docDelta = -1;
                }

                // Check if the modified doc is read or not
                if (!docs[docIdx].reviewed) {
                    readDelta = docDelta;
                } else {
                    readDelta = 0;
                }

                await this.updateQuickCounts("mine", docDelta, readDelta);
            }

            docs[docIdx] = {
                ...docs[docIdx],
                ...doc
            };
        }

        // Updating live document
        if (docId === this.state.document._id)
            await this.setState(prevState => ({
                document: {
                    ...prevState.document,
                    ...doc,
                    lastModified: new Date(),
                },
            }));

        await this.setState({ docs });

        if (!cloud)
            return;

        // API update
        const cloudUpdate = async () => await api.post('/document/update', {
            docId,
            docDiff: {
                ...doc,
                _id: undefined
            }
        });

        if (waitCloud)
            await cloudUpdate();
        else cloudUpdate();

    }

    // Update multiple documents
    updateDocuments = async (docIds, docDiff) => {

        // Update local counts!
        for (let docId of docIds) {
            await this.updateDocument(docId, docDiff, false, false);
        }

        // Local root list update
        let docs = JSON.parse(JSON.stringify(this.state.docs));

        for (let docId of docIds) {
            const docIdx = docs.findIndex(d => d._id === docId);
            docs[docIdx] = {
                ...docs[docIdx],
                ...docDiff
            };
        }

        await this.setState({ docs });

        // API update
        await api.post('/document/update', {
            docIds,
            docDiff
        });

        //await this.setState({ selection: [] });
    }

    // Retrain classifier
    retrainClassifier = async (stack) => {
        await api.post('/stack/train', { stackId: stack._id });
        this.updateStack(stack._id, {
            trainer: {
                ...stack.trainer,
                manualTrigger: true
            }
        }, false);
    }

    // Copy stack
    copyStack = async (source) => {
        await this.props.loading();
        const res = await api.post('/stack/copy', { source });
        if (res && res.success && res.stack && res.stack._id) {
            window.location.href = "/stack/" + res.stack._id;
        }
        this.props.loaded();
    }

    // Move stack
    moveStack = (stack) => this.modal('move-stack', stack);
    moveStackExec = async (stack, parent) => {
        // As simple as renaming the stack! :)
        const pathCmp = stack.path.split("/");
        const name = pathCmp[pathCmp.length - 1];
        const target = parent === "" ? name : parent + "/" + name;
        this.modal();

        await this.renameStackExec(stack, target);
    }

    // Copy documents to
    copyDocumentsTo = (docs) => this.modal('copy-document', docs);

    // Move documents
    moveDocuments = (docs) => this.modal('move-document', docs);
    moveDocumentsExec = async (docs, stack) => {

        let stateDocs = JSON.parse(JSON.stringify(this.state.docs));

        // Remove from documents list
        await this.removeDocumentsLocally(docs, -1,
            d => onPage("/field/") ||
                onPage("/archives") ? false :
                d.stack !== stack);

        // await this.props.loading();
        this.modal();

        this.setState({ selection: [] });

        // Update root stack count data
        for (let docId of docs) {
            const idx = stateDocs.findIndex(d => d._id === docId);
            if (idx < 0) continue;
            const doc = stateDocs[idx];

            // Destination stack
            const dst = this.findStack(stack);
            await this.updateStack(dst._id, {
                numDocuments: dst.numDocuments + 1,
                numUnread: dst.numUnread + (doc.reviewed ? 0 : 1)
            }, false); // Local only


            // Do we need to do this? It causes the state to maintain the doc
            // Update stack in meta
            // stateDocs[idx].stack = stack._id;
        }

        // await this.setState({ docs: stateDocs });

        await api.post('/document/move', {
            docIds: docs,
            stack
        });

        // this.props.loaded();

    }
    spliceDocumentExec = async(name, stack, pages) => {
        // Confirm we have pages, name, and destination. 
        if(!pages.length) return toast.error("No pages selected to splice.");  
        if(!name) return toast.error("No name provided for the new document."); 
        if(!stack) return toast.error("No destination stack provided.");  
        toast.success("Document is being spliced..."); 

        this.modal(); 
        this.setFunction();  
        const res = await api.post("/document/splice", {
            name, 
            stack, 
            pages, 
            docId: this.state.document._id, 
        }); 

        if(!res || res.error) return toast.error("Failed to splice document."); 
        
        // If the desitnation stack is the same as the current stack, we can just add the document to state. 
        if(stack === this.state.stack._id){
            this.setState({docs: [res.doc, ...this.state.docs]})
        }
    }

    // Archive documents
    archiveDocuments = async (docIds, archived = true) => {
        await this.removeDocumentsLocally(docIds, archived ? -1 : 1);
        await api.post('/document/archive', { docIds, archived });
        await this.setState({ selection: [] });
    }

    // Retry failed jobs
    retryFailedJobs = async (docIds,stackId) => {
        toast.info("Jobs on selected documents will be retried...");
        await api.post('/document/retryFailedJobs', { docIds:docIds, stackId:stackId });

        if (this.props.user?.email?.endsWith("@stax.ai")) {
            // Potentially a Stax.ai admin team member - record action
            await Promise.all(docIds.map(async docId => {
                await api.post('/internal/record', {
                    user: this.props.user.email,
                    team: this.props.team.name,
                    code: "Retry",
                    points: 1,
                    document: docId
                });
            }));
        }

        await this.setState({ selection: [] });
    }

    // Re-sort documents
    resortDocuments = async (docIds) => {
        if (onPage('/stack/') && this.state.stack._id.length === 24)
            await this.removeDocumentsLocally(docIds);
        await api.post('/document/resort', { docIds });
        await this.setState({ selection: [] });
    }

    // Re-process documents
    reprocessDocuments = async (docIds) => {
        toast.info("This document will be re-processed shortly...");
        await api.post('/document/retry', { docIds });
        await this.setState({ selection: [] });
    }

    // Re-run the pipeline on the document 
    rerunPipeline = async (docIds) => {
        // Confirm docIds is an array, if not make it one 
        if (!Array.isArray(docIds)) docIds = [docIds]; 
        toast.info(`The pipeline will be re-ran on ${docIds.length > 1? "these documents" : "this document"} shortly...`);
        await api.post('/document/triggerPipeline', { docIds });
    }

    // Delete documents
    removeDocuments = async (docIds) => {
        await this.removeDocumentsLocally(docIds);
        await api.post('/document/remove', { docIds });
        this.setState({ selection: [] });
    }

    // Restore deleted documents
    restoreDocuments = async (docIds) => {
        await this.removeDocumentsLocally(docIds);
        await api.post('/document/restore', { docIds });
        this.setState({ selection: [] })
    }

    // Copy Documents
    copyDocuments = async (docIds, dst=undefined) => {
        await this.props.loading();
        this.modal();
        const res = await api.post('/document/copy', {
            docIds,
            stackId: dst
        });
        let docs = [...this.state.docs];

        if (res && res.docs) {
            // Add new documents to local
            for (const doc of res.docs) {
                // Update stack count
                await this.updateStackCounts(doc, 1);

                // Check if the doc's stack is equal to current stack
                if (doc.stack === this.state.stack._id)
                    docs.unshift(doc);
            }
        }

        // Add document to stateDocs
        this.setState({ selection: [], docs });
        await this.props.loaded();
    }

    // Remove documents locally
    removeDocumentsLocally = async (sel, stackDiff = -1, filter = (() => true)) => {

        let docs = JSON.parse(JSON.stringify(this.state.docs));
        let remaining = sel.slice();

        while (remaining.length > 0) {
            const idx = docs.findIndex(doc => doc._id === remaining[0]);
            remaining.splice(0, 1);

            if (idx >= 0) {
                const doc = this.state.docs[idx];

                // Don't actually move if filter is not met
                if (!filter(doc)) continue;

                this.updateStackCounts(doc, stackDiff, filter);
                docs.splice(idx, 1);
            }
        }

        this.setState({ docs });
        // await this.loadRootData();
    }

    // Update stack counts
    updateStackCounts = async (doc, diff) => {

        // Source stack
        if (doc.stack !== null) {
            const src = this.findStack(doc.stack);
            if (src)
                await this.updateStack(doc.stack, {
                    numDocuments: src.numDocuments + diff,
                    numUnread: src.numUnread + (doc.reviewed ? 0 : diff)
                }, false); // Local only
        }

        // Update unclassified if no stack exists 
        else await this.updateQuickCounts("unclassified", diff, doc.reviewed ? 0 : diff);

        // Check for assignee changes
        if (doc.assignee === this.props.user._id)
            await this.updateQuickCounts("mine", diff, doc.reviewed ? 0 : diff);

        if (doc.flagged)
            await this.updateQuickCounts("flagged", diff, doc.reviewed ? 0 : diff);

        // Update Not Reviewed count in quick
        if (!doc.reviewed)
            await this.updateQuickCounts("unread", diff, diff);
    }

    // Split document by page splits
    splitDocument = async (doc) => {
        if (!doc || !doc.split || doc.split.length < 2)
            return toast.error("Insert at least once splitter between pages.");

        await this.props.loading();
        const res = await api.post('/job/run', {
            moduleId: '604fc8616896c11ace6969aa',
            documentId: doc._id,
        });
        if (res && res.success) {
            toast.info("Processsing... Check back in a few minutes.")
        }

        if (this.props.user?.email?.endsWith("@stax.ai")) {
            // Potentially a Stax.ai admin team member - record split document action
            await api.post('/internal/record', {
                user: this.props.user.email,
                team: this.props.team.name,
                code: "Split",
                points: doc.split.length,
                document: doc._id
            });
        }

        this.props.loaded();
    }

    // Report document error
    reportDocument = async (docId, reason="", notes="") => {
        if (!docId)
            return toast.error("Cannot report an invalid document.");

        await this.props.loading(3000, 'Submitting...');
        this.modal();

        await api.post('/document/report', {
            docId,
            reason,
            notes
        });

        toast.success("Thank you for reporting the error! Someone on our team will take a look at it shortly!");

        this.props.loaded();
    }

    // Train module
    trainModule = async (stack, module, step) => {
        await this.props.loading();
        await api.post('/module/train', {
            stackId: stack._id,
            stepId: step._id,
            moduleId: module._id
        });
        setTimeout(async () => {
            await this.loadRootData();
            this.props.loaded();
            toast.info("The module will start training shortly!");
            toast.info("Training could take a couple of hours. Take a break!");
        }, 1000);
    }

    clearTips = async (tip) => {
        this.props.updateUser({
            _id: this.props.user._id,
            tips: {
                ...this.props.user.tips,
                [tip]: true
            }
        })
    }

    // Find stack by Id
    findStack = (stackId) => this.state.root.stacks.find(stack => stack._id === stackId);

    // Edit module config
    editModuleConfig = async (stack, stepIdx, module) => {
        this.modal("automate-configure", {
            module,
            stack,
            reload: this.loadRootData,
            edit: stepIdx
        });
    }

    // Detach module from stack
    detachModule = async (stack, module, step) => {
        await this.props.loading();
        const res = await api.post('/module/detach', {
            stackId: stack._id,
            moduleId: module._id,
            stepId: step._id
        });
        if (res && res.success) {
            // Clear from local cache
            const pipeline = [...stack.pipeline];
            const moduleIdx = pipeline.findIndex(s => s._id === step._id);
            if (moduleIdx < 0)
                return;
            pipeline.splice(moduleIdx, 1);
            this.updateStack(stack._id, { pipeline }, false);
        }
        this.props.loaded();
    }

    // Clear user created field tags from state
    clearNewTag = async () => {
        await this.setState({
            document: {
                ...this.state.document,
                newTag: {}
            }
        })
    }

    // Called to make sure user has a payment method added
    checkActivationStatus = async () => {
        const res = await api.get('/team/billing/active');
        if (res && !res.active && !this.props?.user?.email?.includes("stax.ai"))
            this.modal("trial-expired");
    }

    addPaymentMethod = async (card, details) => {
        const res = await api.post('/team/billing/method/create', {
            type: "card",
            method: {
                card: {
                    number: card.number,
                    exp_month: card.expiry.substr(0, 2),
                    exp_year: card.expiry.substr(3),
                    cvc: card.cvc
                },
                billing_details: {
                    ...details,
                    email: this.props.user.email
                }
            }
        });
        if (!res || !res.success || !res.method) return false;

        toast.success("Successfully added new credit card!");

        window.location.href = '/settings/billing';
    }

    //TODO: @kyle Johnson
    saveTableChanges = async (table) => {
        // Un rotate the table if needed 
        if(Object.keys(this.state.document.rotations??{}).length > 0){
            const rotations = {...Object.keys(this.state.document.rotations??{}).map((key) => {
                return this.state.document.rotations[key] * -1
            })}

            table = rotateTable(table, rotations) 
        }

        // Set the state with the updated table, keep rotations if any 
        const tables = this.state.document.tables.map((t, i) => i === this.state.editTableIdx ? {...table, changed: true} : t);
        this.setState({ document: { ...this.state.document, tables } });
        // this.updateDocument(this.state.document._id, { tables }, true);
    }

    uploadAttachment = async (docId, doc, cloud = true, waitCloud = true) => {

        this.fileUploadDialog.click();
        this.fileUploadDialog.onchange = async (e) => {
            let path;
            if (e.path)
                path = e.path;
            else if (e.composedPath && e.composedPath())
                path = e.composedPath()
            else
                return; // raise toast error here?
            
            await this.props.loading(1005, "Uploading...");
            
            let formData = new FormData();
            formData.set('file', path[0].files[0]);
            const res = await api.post('/document/attach?docId=' + docId, formData);
            if (res && res.key && res.key.length > 10) {
                const pathCmp = res.key.split("/");
                const filename = pathCmp[pathCmp.length-1];
                let attachments = doc?.attachments ? doc.attachments : [];
                attachments.push({
                    filename,
                    filepath: res.key
                });
                await this.updateDocument(docId, { attachments }, true);
            } else toast.error("Failed to attach file");

            this.props.loaded();

            toast.success("Attachment successfully uploaded! ")
 
   
        }


    }

    render = () => {
        const quick = this.state.root.quick.filter(val => val.numDocuments > 0).concat(this.state.root.stacks.filter(stack => stack.pinned));
        const user = this.props.user;
        const team = this.props.team;
        const stack = this.state.stack;
        // const unclassified = this.state.root.quick.find(s => s._id === "unclassified");
        const hideMetaPages = !onPage("/stack/") && !onPage("/archives") && !onPage('/trash');
        const hideMeta = hideMetaPages || this.state.meta === 'hide';

        // Window title
        if (onPage("/document/") && this.state.document.metadata){
            const accountMeta = this.state.document.metadata.find(meta => meta.key === 'account');
            const periodStartMeta = this.state.document.metadata.find(meta => meta.key === 'period start');
            if (accountMeta && periodStartMeta) {
                document.title = `${accountMeta.value} : ${periodStartMeta.value.split(" ")[0]} - Stax·ai`;
            } else if (this.state.document && this.state.document.name) {
                document.title = this.state.document.name + ' - Stax·ai';
            }
        }
        else if (onPage("/document/") && this.state.document && this.state.document.name)
            document.title = this.state.document.name + ' - Stax·ai';

        else if (onPage("/stack/") && stack && stack.path)
            document.title = stack.path + ' - Stax·ai';

        else document.title = "My Stacks - Stax·ai";

        let helper = "";
        // const docs = this.state.docs;
        // const unclassified = this.state.root.quick.find(s => s._id === "unclassified");
        // Helpers for standard stack uploads
        // if (user.isAdmin && onPage("stack/") && stack && stack.enabled && !(stack.rules && stack.rules.enabled)) {
        //     if (docs.length >= 5 && stack.trainer && !stack.trainer.lastTrained && !stack.trainer.manualTrigger)
        //         helper = "You have enough documents to train this stack but it's not yet trained!<br />You can ask it to train right now by clicking <strong>Retrain Classifier</strong> under <strong>Actions</strong> (<i>right side</i>), or it will train on its own <u>tonight</u>.";

        //     else if (docs.length > 0 && docs.length < 5)
        //         helper = stack.base ? "The bots will sort documents into this stack using to the pre-trained model you selected. However, you can upload your own examples here and the bots will learn to sort your specific documents. Upload at least <strong>five</strong> if you have them." : "You need <u>at least</u> <strong>five</strong> documents in this stack for the bots to train on it. Once you upload them, click on <strong>Retrain Classifier</strong> under <strong>Actions</strong> (<i>right side</i>).";

        //     else if (!stack.base && docs.length >= 5 && docs.length < 10)
        //         helper = "Great job! The bots can start helping you with 5 documents, but they get <strong>much</strong> better at sorting with more. Try to get at least <strong>ten</strong>.";

        //     else if (stack.numMovedFiles > 0)
        //         helper = "You have moved or uploaded some documents into this stack. The bots won't retrain automatically until tonight. Click on <strong>Retrain Classifier</strong> under <strong>Actions</strong> (<i>right side</i>) to get the bots to retrain now.";
        // }

        // else if (user.isAdmin && !onPage('stack') && !this.state.root.stacks.length)
        //     helper = "You need to create a <strong>stack</strong> for each category of document you'd like sorted. Make sure you have a <strong>few examples</strong> of documents for each, or pick from one of our pre-trained models.<br />Click on the <strong>plus</strong> sign above and I'll take you through the process!";

        // else if (unclassified && unclassified.numDocuments > 0)
        //     helper = "Uh Oh... You have some unclassified documents. Click on <strong>Unclassified</strong> under <strong>Quick Access</strong> to move them into the right stacks. When the bots retrain, they'll sort similar documents accordingly!";

        return (<Router>

            {/* Navigation Bar */}
            <Navigation
                user={this.props.user}
                team={this.props.team}
                unread={this.state.root.quick.find(s => s._id === "unread")}
                assigned={this.state.root.quick.find(s => s._id === "mine")}
            />

            {/* User & Notifications */}
            <User
                clearNotification={this.props.clearNotification}
                user={this.props.user}
                updateUser={this.props.updateUser}
                switchTeam={this.props.switchTeam}
                team={this.props.team}
            />

            {/* Global Searchbar */}
            <Search
                user={this.props.user}
                query={this.state.search}
                activeZ={this.state.searchArchive ? 1000 : 1005}
                search={this.search}
                searching={this.state.searching}
                placeholder={onPage('/document/') ? 'Search within file...' : onPage('/stack/') ? 'Search in stack...' : onPage('/field/') ? 'Search filtered files...' : 'Search all files...'}
            />

            {/* Stack search results */}
            {(this.state.search !== '' || (this.state.searchArchive) )&& !onPage('document') && <SearchResults
                query={this.state.search}
                stack={stack}
                stacks={this.state.root.stacks}
                search={this.search}
                downloadDocument={this.downloadDocument}
                printDocument={this.printDocument}
                sendDocument={this.sendDocument}
                moveDocuments={this.moveDocuments}
                removeDocuments={this.removeDocuments}
                resortDocuments={this.resortDocuments}
                updateDocuments={this.updateDocuments}
                archive={this.state.searchArchive}
                clearArchiveSearch={() => {
                    this.setState({ searchArchive: null })
                    if (window.location.pathname.includes("/search/")) {
                        this.props.history.push(window.location.pathname.split("/search/")[0])
                    }
                }}
                field={this.state.field.key}
                value={this.state.value}
            />}

            <div className={"main-title" + (hideMeta ? " main-title-nometa"
                : "")}>
                <Switch>
                    <Route path={["/", "/search/:query"]} exact render={() => <>
                        <div className="title-text">My Stacks</div>
                        <div
                            className="title-icon"
                            onClick={() => this.createStack()}
                        >
                            Create a new stack
                        </div>
                        <ViewControl view={this.state.view} setView={view => this.setState({ view })} />
                    </>} />

                    {/* Archived files */}
                    <Route path="/archives" exact render={() => <>
                        <div className="title-text">Archived Documents</div>
                        <ViewControl view={this.state.view} setView={view => this.setState({ view })} />
                    </>} />

                    {/* Deleted files */}
                    <Route path="/trash" exact render={() => <>
                        <div className="title-text">Deleted Documents</div>
                        <ViewControl view={this.state.view} setView={view => this.setState({ view })} />
                    </>} />

                    {/* Fields */}
                    <Route path="/fields" exact render={() => <>
                        <div className="title-text">Filter Documents</div>
                    </>} />

                    {/* Field value */}
                    <Route path={["/field/:field/:value", "/field/:field/:value/search/:query"]} exact render={props => <FieldName
                        {...props}
                        view={this.state.view}
                        setView={view => this.setState({ view })}
                    />} />

                    {/* Stack folders (including archives) */}
                    <Route
                        path={["/stack/:stackId", "/stack/:stackId/search/:query"]}
                        exact render={props => 
                        <StackName
                            {...props}
                            stack={stack}
                            setStack={stack => this.setState({ stack })}
                            stacks={this.state.root.stacks}
                            view={this.state.view}
                            setView={view => this.setState({ view })}
                            openMenu={this.openMenu}
                            uploadFiles={() => this.uploadFiles(stack._id)}
                            user={this.props.user}
                            updateUser={this.props.updateUser}
                        />
                    }
                    />

                </Switch>
            </div>

            {/* Stack operations (search results, etc.) */}
            <Route
                path={["/", "/search/:query", "/stack/:stackId", "/stack/:stackId/search/:query", "/field/:field/:value", "/field/:field/:value/search/:query"]} exact
                render={props => <StacksOps
                    {...props}
                    search={this.search}
                    clearSelection={() => this.setState({ selection: [] })}
                    live={this.startStatusUpdateRoutine}
                />}
            />

            <div className={"main-content" + (hideMeta ? " main-content-nometa"
                : "")}>
                <Scrollbars ref={this.scrollRef} onScroll={this.handleScroll}
                    renderTrackVertical={props => <div className="scrollbar-track-v" {...props} />}
                    renderTrackHorizontal={props => <div className="scrollbar-track-h" {...props} />}
                    renderThumbVertical={props => <div className="scrollbar-thumb-v" {...props}  />}
                    renderThumbHorizontal={props => <div className="scrollbar-thumb-h" {...props} />}
                >

                    {/* Alert */}
                    {this.state.alert && <div className="alert">
                        <img
                            src="https://static.stax.ai/images/icons/close.png"
                            alt="x"
                            title="Clear alert"
                            onClick={() => this.setState({ alert: "" })}
                        />
                        {HTML(this.state.alert)}
                    </div>}

                    {/* Helper message */}
                    {stack && !stack.hideHint && helper && helper !== "" && <div
                        className="alert helper"
                    >
                        {HTML(helper)}
                        {stack.path !== "" ? <div
                            className="helper-hide-button"
                            onClick={() => this.updateStack(stack._id, {
                                hideHint: true
                            })}
                        >Don't show here again.</div> : ""}
                    </div>}


                    {/* Content routing */}
                    {/* Home page: Quick Access, All Stacks */}
                    {quick && quick.length > 0 && <Route
                        path={["/", "/search/:query"]} exact
                        render={props => <Stacks
                            {...props}
                            title="Quick Access"
                            stacks={quick}
                            view={this.state.view}
                            uploadFiles={() => this.uploadFiles()}
                            createStack={this.createStack}
                            moveStack={() => { }}
                            copyStack={() => { }}
                            user={user}
                            team={team}
                            modal={this.modal}
                        />}
                    />}

                    <Route
                        path={["/", "/search/:query"]} exact
                        render={props => <Stacks
                            {...props}
                            user={user}
                            team={team}
                            title="My Stacks"
                            creatable={true}
                            stacks={this.state.root.stacks}
                            view={this.state.view}
                            uploadFiles={this.uploadFiles}
                            uploadFilesExec={this.uploadFilesExec}
                            createStack={this.createStack}
                            deleteStack={this.deleteStack}
                            updateStack={this.updateStack}
                            renameStack={this.renameStack}
                            moveStack={this.moveStack}
                            copyStack={this.copyStack}
                            selectedStack={this.state.modalData ? this.state.modalData._id : null}
                            modal={this.modal}
                        />}
                    />

                    {/* Sub-stacks within a stack */}
                    {stack && stack._id && stack._id.length === 24 && <Route
                        path={["/stack/:stackId", "/stack/:stackId/search/:query"]}
                        render={props => <Stacks
                            {...props}
                            user={user}
                            team={team}
                            title="Nested Stacks"
                            creatable={true}
                            stacks={this.state.root.stacks}
                            view={this.state.view}
                            uploadFiles={this.uploadFiles}
                            uploadFilesExec={this.uploadFilesExec}
                            moveStack={this.moveStack}
                            copyStack={this.copyStack}
                            createStack={this.createStack}
                            deleteStack={this.deleteStack}
                            updateStack={this.updateStack}
                            renameStack={this.renameStack}
                            selectedStack={this.state.modalData ? this.state.modalData._id : null}
                            modal={this.modal}
                        />}
                    />}

                    {/* Inside a stack, Archives, or Trash */}
                    <Route
                        path={["/stack/:stackId", "/archives", "/trash", "/field/:field/:value"]}
                        render={props => <Documents
                            {...props}
                            scrollPosition={this.state.scrollPosition}
                            loading={this.props.loading}
                            loaded={this.props.loaded}
                            view={this.state.view}
                            user={user}
                            team={team}
                            modal={this.modal}
                            alert={alert => this.setState({ alert })}
                            stacks={[...this.state.root.stacks, ...this.state.root.quick]}
                            stack={stack}
                            setStack={stack => this.setState({ stack })}
                            setNArchived={nArchived => this.setState({ nArchived })}
                            setNDeleted={nDeleted => this.setState({ nDeleted })}
                            nDeleted={this.state.nDeleted}
                            setFieldValue={(f, v) => this.setState({
                                field: {
                                    ...this.state.field,
                                    key: f
                                },
                                value: v
                            })}
                            docs={this.state.docs}
                            setDocs={async docs => await this.setState({ docs })}
                            filter={this.state.filter}
                            clearFilter={() => this.setState({ filter: [] })}
                            createStack={this.createStack}
                            setDocument={this.setDocument}
                            updateDocument={this.updateDocument}
                            updateDocuments={this.updateDocuments}
                            renameDocument={this.renameDocument}
                            downloadDocument={this.downloadDocument}
                            printDocument={this.printDocument}
                            sendDocument={this.sendDocument}
                            moveDocuments={this.moveDocuments}
                            copyDocuments={this.copyDocuments}
                            copyDocumentsTo={this.copyDocumentsTo}
                            archiveDocuments={this.archiveDocuments}
                            removeDocuments={this.removeDocuments}
                            restoreDocuments={this.restoreDocuments}
                            resortDocuments={this.resortDocuments}
                            reprocessDocuments={this.reprocessDocuments}
                            rerunPipeline={this.rerunPipeline}
                            selection={this.state.selection}
                            select={async selection => await this.setState({ selection })}
                            uploadFiles={() => this.uploadFiles(stack._id)}
                            uploadFilesDirect={this.uploadFiles}
                            uploadFilesExec={this.uploadFilesExec}
                            retryFailedJobs={this.retryFailedJobs}
                        />}
                    />

                    {/* Fields selector/filter */}
                    <Route
                        path="/fields"
                        exact
                        render={props => <MyFields
                            {...props}
                            user={this.props.user}
                            loading={this.props.loading}
                            loaded={this.props.loaded}
                            field={this.state.field}
                            fields={this.state.fields}
                            setFields={fields => this.setState({ fields })}
                            selectField={field => this.setState({ field })}
                        />}
                    />

                </Scrollbars>
            </div>

            <div className={"meta-content" + (hideMeta ? " meta-content-nometa"
                : "")}>
                <Scrollbars
                    renderTrackVertical={props => <div className="scrollbar-track-v" {...props} />}
                    renderTrackHorizontal={props => <div className="scrollbar-track-h" {...props} />}
                    renderThumbVertical={props => <div className="scrollbar-thumb-v" {...props} />}
                    renderThumbHorizontal={props => <div className="scrollbar-thumb-h" {...props} />}
                >

                    {/* Meta routing */}
                    {/* Home page: Most Recent */}
                    <Route
                        path={["/", "/search/:query"]} exact
                        render={props => <MostRecent
                            {...props}
                            uploadFiles={() => this.uploadFiles()}
                            createStack={this.createStack}
                            user={user}
                            loading={this.props.loading}
                            loaded={this.props.loaded}
                        />}
                    />

                    {/* Stack/Document Meta */}
                    <Route
                        path={["/stack/:stackId", "/field/:field/:value"]}
                        render={props => <Meta
                            {...props}
                            stack={stack}
                            stacks={this.state.root.stacks}
                            document={this.state.document}
                            statement={this.state.statement}
                            setStatement={this.setStatement}
                            clearNewTag={this.clearNewTag}
                            docs={this.state.docs}
                            filter={this.state.filter}
                            setFilter={filter => this.setState({ filter })}
                            selection={this.state.selection}
                            select={async selection => await this.setState({ selection })}
                            modules={this.state.modules}
                            forms={this.state.forms}
                            user={user}
                            team={team}
                            notify={this.props.notify}
                            modal={this.modal}
                            loading={this.props.loading}
                            loaded={this.props.loaded}
                            hover={hovered => this.setState({ hovered })}

                            // Edit functions
                            uploadFiles={() => this.uploadFiles(stack._id)}
                            moveStack={() => this.moveStack(stack._id)}
                            deleteStack={() => this.deleteStack(stack._id, true)}
                            uploadFilesDirect={this.uploadFiles}
                            updateStack={this.updateStack}
                            manageSharing={this.manageSharing}
                            manageDocumentSharing={this.manageDocumentSharing}
                            manageSorting={this.manageSorting}
                            createStack={this.createStack}
                            renameStack={this.renameStack}
                            renameStackExec={this.renameStackExec}
                            detachModule={this.detachModule}
                            editModuleConfig={this.editModuleConfig}
                            renameDocument={this.renameDocument}
                            updateDocument={this.updateDocument}
                            updateDocuments={this.updateDocuments}
                            downloadDocument={this.downloadDocument}
                            printDocument={this.printDocument}
                            sendDocument={this.sendDocument}
                            moveDocuments={this.moveDocuments}
                            archiveDocuments={this.archiveDocuments}
                            removeDocuments={this.removeDocuments}
                            splitDocument={this.splitDocument}
                            retrainClassifier={this.retrainClassifier}
                            trainModule={this.trainModule}
                            setEditTableIdx = {(idx) => this.setState({editTableIdx: idx})}
                            editTableIdx={this.state.editTableIdx}
                            uploadAttachment={this.uploadAttachment}

                            // Custom functions
                            function={this.state.function}
                            functionData={this.state.functionData}
                            setFunction={this.setFunction}
                        />}
                    />

                    {/* Archive Search */}
                    <Route
                        path="/archives"
                        render={props => <SearchArchives
                            {...props}
                            user={this.props.user}
                            nArchived={this.state.nArchived}
                            pickDate={(callback) => this.modal("date-range", callback)}
                            search={(searchArchive) => this.setState({ searchArchive })}
                            location='/archives'
                        />}
                    />

                    <Route 
                        path="/trash"
                        render={props => <TrashMeta 
                            {...props}
                            user={this.props.user}
                            team={this.props.team}
                            docs={this.state.docs}
                            stacks={this.state.root.stacks}
                            selection={this.state.selection}
                            nDeleted={this.state.nDeleted}
                            location='/trash'
                        />}
                    />

                    {/* Fields documentation */}
                    <Route
                        path="/fields"
                        render={props => <FieldsMeta
                            {...props}
                            user={this.props.user}
                            fields={this.state.fields}
                        />}
                    />

                </Scrollbars>
            </div>

            {/* Document redirect */}
            <Route
                path="/document/:documentId" exact
                render={props => <DocumentRedirect
                    {...props}
                    user={user}
                    loading={this.props.loading}
                />}
            />

            {/* File viewer */}
            <Route
                path={["/stack/:stackId/document/:documentId", "/stack/:stackId/document/:documentId/search/:query", "/field/:field/:value/document/:documentId", "/field/:field/:value/document/:documentId/search/:query"]} exact
                render={props => <FileViewer
                    {...props}
                    format={this.state.document ? this.state.document.format : "doc"}
                    hideMeta={hideMeta}
                    user={user}
                    team={team}
                    modal={this.modal}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                    updateUser={this.props.updateUser}
                    stack={stack}
                    document={this.state.document}
                    docs={this.state.docs}
                    setDocument={this.setDocument}
                    setStatement={this.setStatement}
                    setWords={this.setWords}
                    rotatePage={this.rotateDocumentPage}
                    notify={this.props.notify}
                    search={this.search}
                    query={this.state.search}
                    searching={this.state.searching}
                    hovered={this.state.hovered}
                    matches={this.state.matches}
                    gotoMatch={index => this.setState({ matches: { ...this.state.matches, index } })}

                    // Operations
                    viewDoc={this.viewDoc}
                    updateDocument={(diff, cloud = true) => this.updateDocument(this.state.document._id, diff, cloud)}
                    renameDocument={() => this.renameDocument(this.state.document)}
                    downloadDocument={() => this.downloadDocument(this.state.document)}
                    printDocument={() => this.printDocument(this.state.document)}
                    sendDocument={() => this.sendDocument(this.state.document)}
                    moveDocument={() => this.moveDocuments([this.state.document._id])}
                    copyDocumentTo={() => this.copyDocumentsTo([this.state.document._id])}
                    archiveDocument={(archived = true) => this.archiveDocuments([this.state.document._id], archived)}
                    removeDocument={() => this.removeDocuments([this.state.document._id])}
                    rerunPipeline={() => this.rerunPipeline(this.state.document._id)}
                    editTableIdx={this.state.editTableIdx}
                    closeTableEditor={() => this.setState({editTableIdx: -1})}
                    saveTableChanges={(table) => this.saveTableChanges(table)}

                    // Functions
                    function={this.state.function}
                    functionData={this.state.functionData}
                    setFunction={this.setFunction}
                    reprocessDocuments={this.reprocessDocuments}
                    clearSelection={(doc) => this.setState({ selection: [doc] })}
                />}
            />

            {/* Meta hide/view control */}
            {!hideMetaPages && <div
                className={"meta-control" + (hideMeta ? " meta-control-nometa" : "")}
                onClick={() => this.setState({
                    meta: this.state.meta === 'hide' ? 'show' : 'hide'
                })}
            />}

            {/* Options context menu control */}
            <ContextMenu
                load={(open, close) => {
                    this.openMenu = open;
                    this.closeMenu = close;
                }}
                options={[
                    {
                        label: 'Select all visible',
                        action: () => this.setState({
                            selection: this.state.docs.map(d => d._id)
                        })
                    },
                    {
                        label: 'Select all',
                        action: async () => {
                            const resp = await api.get('/stack/docs?stackId=' + this.state.stack?._id);
                            this.setState({ selection : resp.docs })
                        }
                    },
                    {
                        label: 'Unselect all',
                        action: () => this.setState({
                            selection: [] 
                        })
                    }
                ]}
            />

            {/* Modal controller */}
            {/* Create a new stack */}
            {this.state.modal === "create-stack" ?
                <CreateStack
                    modal={this.modal}
                    data={this.state.modalData}
                    createStackExec={this.createStackExec}
                /> :

            // Pick pretrained model
            this.state.modal === "pretrained-model" ?
                <PretrainedModel
                    modal={this.modal}
                    stacks={this.state.root.stacks}
                    data={this.state.modalData}
                    createStackExec={this.createStackExec}
                /> :
            
            this.state.modal === "workflowTemplate-model" ?
                <WorkflowTemplate
                    modal={this.modal}
                    data={this.state.modalData}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                    team={team}
                    stacks={this.state.stacks}
                    loadStacks={this.loadStacks}
                    templates={this.state.templates}
                    setTemplates={templates => this.setState({ templates })}
                /> :

            // Rename stack
            this.state.modal === "rename-stack" ?
                <RenameStack
                    modal={this.modal}
                    data={this.state.modalData}
                    rename={this.renameStackExec}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                /> :

            // Rename document
            this.state.modal === "rename-document" ?
                <RenameDocument
                    modal={this.modal}
                    data={this.state.modalData}
                    updateDocument={this.updateDocument}
                /> :

            // Send document
            this.state.modal === "send-document" ?
                <SendDocument
                    modal={this.modal}
                    data={this.state.modalData}
                    sendDocumentExec={this.sendDocumentExec}
                /> :

            this.state.modal === 'splice-document' ? 
                <SpliceDocument
                    modal={this.modal}
                    data={this.state.modalData}
                    spliceDocumentExec={this.spliceDocumentExec}
                    stack={stack}
                    stacks={this.state.root.stacks}  
                    document={this.state.document} 
                /> :

            // Move document
            this.state.modal === "move-document" ?
                <MoveDocument
                    modal={this.modal}
                    data={this.state.modalData}
                    stack={stack}
                    stacks={this.state.root.stacks}
                    move={this.moveDocumentsExec}
                /> :

            // Copy document to
            this.state.modal === "copy-document" ?
                <MoveDocument
                    modal={this.modal}
                    data={this.state.modalData}
                    stack={stack}
                    stacks={this.state.root.stacks}
                    move={this.copyDocuments}
                    title={"Copy your"}
                    description={"Select or drop the documents over the stack you'd like to copy them to."}
                    buttonText="Copy"
                /> :

            // Move stack
            this.state.modal === "move-stack" ?
                <MoveStack
                    modal={this.modal}
                    data={this.state.modalData}
                    stack={stack}
                    stacks={this.state.root.stacks}
                    move={this.moveStackExec}
                /> :

            // Stack sharing settings
            this.state.modal === "sharing-settings" ?
                <SharingSettings
                    modal={this.modal}
                    stack={stack}
                    updateStack={this.updateStack}
                    team={this.props.team}
                /> :

            // Document sharing settings
            this.state.modal === "document-sharing" ?
                <DocumentSharing
                    modal={this.modal}
                    data={this.state.modalData}
                    getDoc={this.getDocument}
                    updateDocument={this.updateDocument}
                    team={this.props.team}
                /> :

            // Stack sorting rules
            this.state.modal === "sorting-rules" ?
                <SortingRules
                    modal={this.modal}
                    stack={stack}
                    updateStack={this.updateStack}
                /> :

            // Batch file upload
            this.state.modal === "batch-upload" ?
                <BatchUpload
                    modal={this.modal}
                    data={this.state.modalData}
                    team={this.props.team}
                    stack={stack}
                    registerUpload={this.addUploadsLocally}
                    loadRootData={this.loadRootData}
                /> :

            // Date range picker
            this.state.modal === "date-range" ?
                <DateRange
                    modal={this.modal}
                    select={(from, to) => {
                        this.state.modalData(from, to);
                        this.modal();
                    }}
                /> :

            // Configure module
            this.state.modal === "automate-configure" ?
                <ConfigureModule
                    modal={this.modal}
                    data={this.state.modalData}
                    team={this.props.team}
                    user={this.props.user}
                    users={this.props.team.users}
                    stacks={this.state.root.stacks}
                /> :

            // View/edit field using form
            this.state.modal === "form-field" ?
                <FieldForm
                    modal={this.modal}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                    data={this.state.modalData}
                    updateDocument={this.updateDocument}
                /> :

            // Confirm action/caution
            this.state.modal === "confirm" ?
                <ConfirmCaution
                    modal={this.modal}
                    data={this.state.modalData}
                /> :

            // Report document
            this.state.modal === "report-document" ?
                <ReportDocument
                    modal={this.modal}
                    data={this.state.modalData}
                    reportDocument={this.reportDocument}
                /> :

            // Require payment method
            this.state.modal === "trial-expired" ?
                <TrialExpired
                    noClose={true}
                    modal={this.modal}
                    user={this.props.user}
                    team={this.props.team}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                    addPaymentMethod={this.addPaymentMethod}
                /> : 
            this.state.modal === "attach-info" ?
                <AttachInfo
                    modal={this.modal}
                    data={this.state.modalData}
                    loading={this.props.loading}
                    loaded={this.props.loaded}
                    document={this.state.document}
                    updateDocument={this.updateDocument}
                    team={team}
                    user={user}
                />:""}

            {/* Tips */}
            {onPage("stack") && !onPage("document") && stack && stack._id && user && (!user.tips || !user.tips.stack) && <StackTips
                close={() => this.clearTips("stack")}
            />}

            {onPage("document") && this.state.document && this.state.document._id && user && (!user.tips || !user.tips.document) && <DocumentTips
                close={() => this.clearTips("document")}
            />}

            {onPage("/stack/unclassified") && this.state.docs && this.state.docs.length > 0 && user && (!user.tips || !user.tips.unclassified) && <UnclassifiedTips
                close={() => this.clearTips("unclassified")}
            />}

        </Router>);
    }

}